import { computeTotals, type LineKind } from '../modules/docs/types/documents.types';
import { primarySurvey } from './project-workspace/workspaceQuote.helpers';
import type { BoardCard } from './project-workspace/workspaceDashboard.model';
import { OPERATIONAL_QUEUES } from './project-workspace/operationalState';

export type WorkspaceExportRow = {
  workspace: string;
  workspaceHref?: string;
  customer: string;
  email: string;
  lifecycle: string;
  lifecycleRef: string;
  lifecycleRefHref?: string;
  status: string;
  updated: string;
};

export type LinkedLifecycleDoc = {
  id: string;
  project_id?: string | null;
  lifecycle_ref?: string | null;
  kind?: string | null;
  created_at?: string | null;
};

function kindRank(kind?: string | null) {
  if (kind === 'Quotation') return 2;
  if (kind === 'Estimate') return 1;
  return 0;
}

export function pickLifecycleRefByProject(
  docs: LinkedLifecycleDoc[]
): Record<string, { id: string; ref: string }> {
  const chosen: Record<string, { id: string; ref: string; rank: number; at: number }> = {};
  for (const doc of docs) {
    const projectId = String(doc.project_id || '').trim();
    const ref = String(doc.lifecycle_ref || '').trim();
    const id = String(doc.id || '').trim();
    if (!projectId || !ref || !id) continue;
    const next = {
      id,
      ref,
      rank: kindRank(doc.kind),
      at: Date.parse(doc.created_at || '') || 0,
    };
    const previous = chosen[projectId];
    if (!previous || next.rank > previous.rank || (next.rank === previous.rank && next.at >= previous.at)) {
      chosen[projectId] = next;
    }
  }
  return Object.fromEntries(
    Object.entries(chosen).map(([projectId, value]) => [projectId, { id: value.id, ref: value.ref }])
  );
}

export type WorkspaceSurveyRow = {
  project_id?: string | null;
  kind?: string | null;
  option_id?: string | null;
  document_version_id?: string | null;
};

export type SurveyVersionHeader = {
  id: string;
  admin_pct?: number | string | null;
  admin_cap?: number | string | null;
  contingency_pct?: number | string | null;
  profit_pct?: number | string | null;
  vat_enabled?: boolean | null;
  vat_pct?: number | string | null;
};

export type SurveyVersionLine = {
  version_id: string;
  line_kind: string;
  qty?: number | string | null;
  unit_price?: number | string | null;
  line_total?: number | string | null;
  vat_inclusive?: boolean | null;
};

/** Final Survey pricing record when the workspace has one, else its First Survey record. */
export function pickSurveyVersionByProject(surveys: WorkspaceSurveyRow[]): Record<string, string> {
  const byProject: Record<string, { kind: 'first' | 'final'; optionId: string | null; versionId: string }[]> = {};
  for (const survey of surveys) {
    const projectId = String(survey.project_id || '').trim();
    const versionId = String(survey.document_version_id || '').trim();
    if (!projectId || !versionId || (survey.kind !== 'first' && survey.kind !== 'final')) continue;
    (byProject[projectId] ||= []).push({ kind: survey.kind, optionId: survey.option_id || null, versionId });
  }
  const picked: Record<string, string> = {};
  for (const [projectId, rows] of Object.entries(byProject)) {
    const survey = primarySurvey(rows, 'final') || primarySurvey(rows, 'first');
    if (survey) picked[projectId] = survey.versionId;
  }
  return picked;
}

export function surveyVersionGrossTotal(header: SurveyVersionHeader, lines: SurveyVersionLine[]): number {
  return computeTotals(
    lines.map((line) => ({
      lineKind: line.line_kind as LineKind,
      qty: Number(line.qty ?? 0),
      unitPrice: Number(line.unit_price ?? 0),
      lineTotal: line.line_total != null ? Number(line.line_total) : undefined,
      vatInclusive: !!line.vat_inclusive,
    })),
    {
      adminPct: Number(header.admin_pct ?? 0),
      adminCap: Number(header.admin_cap ?? 0),
      contingencyPct: Number(header.contingency_pct ?? 0),
      profitPct: Number(header.profit_pct ?? 0),
      developerPct: null,
    },
    { enabled: !!header.vat_enabled, pct: Number(header.vat_pct ?? 0.2) },
  ).grandTotal;
}

export function xmlEscape(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function xlsCell(value: string, href?: string) {
  const data = `<Data ss:Type="String">${xmlEscape(value)}</Data>`;
  if (href) {
    return `<Cell ss:StyleID="Hyperlink" ss:HRef="${xmlEscape(href)}">${data}</Cell>`;
  }
  return `<Cell>${data}</Cell>`;
}

export function workspaceDashboardXls(rows: WorkspaceExportRow[]): string {
  return xlsWorkbook(
    'Workspaces',
    ['Workspace', 'Customer', 'Email', 'Lifecycle', 'Lifecycle ref', 'Status', 'Updated'],
    rows.map((row) => [
      xlsCell(row.workspace, row.workspaceHref),
      xlsCell(row.customer),
      xlsCell(row.email),
      xlsCell(row.lifecycle),
      xlsCell(row.lifecycleRef, row.lifecycleRefHref),
      xlsCell(row.status),
      xlsCell(row.updated),
    ]),
  );
}

export type JobListExportRow = {
  date: string;
  dateKind: string;
  workspace: string;
  workspaceHref?: string;
  customer: string;
  /** Live Job only. */
  stage?: string;
  status: string;
  flags: string;
  next: string;
};

const QUEUE_LABEL: Record<string, string> = Object.fromEntries(OPERATIONAL_QUEUES.map((queue) => [queue.id, queue.label]));

/** Next step as the tab shows it: who it is waiting on, then why. */
export function boardCardNext(card: Pick<BoardCard, 'note' | 'stateLabel' | 'flag'>): string {
  if (!card.note || card.note === card.stateLabel) return '';
  return `${card.flag === 'office' ? 'Office' : 'Customer'} · ${card.note}`;
}

export function jobListExportRows(cards: BoardCard[], origin: string): JobListExportRow[] {
  return cards.map((card) => {
    const [year, month, day] = (card.keyDate?.day || '').split('-');
    return {
      date: card.keyDate ? `${day}/${month}/${year}` : '',
      dateKind: card.keyDate?.label || '',
      workspace: card.workspace,
      workspaceHref: `${origin}${card.href}`,
      customer: card.customer,
      stage: QUEUE_LABEL[card.queue] || card.queue,
      status: card.stateLabel,
      flags: card.flags.join(', '),
      next: boardCardNext(card),
    };
  });
}

/** The Live Job or Pre-Sale tab as shown: same rows, same order. */
export function jobListXls(sheet: string, rows: JobListExportRow[], withStage: boolean): string {
  const header = ['Date', 'Date is', 'Workspace', 'Customer', ...(withStage ? ['Stage'] : []), 'Status', 'Flags', 'Next'];
  return xlsWorkbook(sheet, header, rows.map((row) => [
    xlsCell(row.date),
    xlsCell(row.dateKind),
    xlsCell(row.workspace, row.workspaceHref),
    xlsCell(row.customer),
    ...(withStage ? [xlsCell(row.stage || '')] : []),
    xlsCell(row.status),
    xlsCell(row.flags),
    xlsCell(row.next),
  ]));
}

function xlsWorkbook(sheet: string, header: string[], rows: string[][]): string {
  const body = rows.map((cells) => `<Row>${cells.join('')}</Row>`).join('');
  return `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
  <Styles>
    <Style ss:ID="Hyperlink" ss:Name="Hyperlink">
      <Font ss:Color="#0563C1" ss:Underline="Single"/>
    </Style>
  </Styles>
  <Worksheet ss:Name="${xmlEscape(sheet)}">
    <Table>
      <Row>${header.map((label) => xlsCell(label)).join('')}</Row>
      ${body}
    </Table>
  </Worksheet>
</Workbook>
`;
}
